// Deep import: the image barrel pulls the whole element tree back in,
// which makes this module unimportable on its own (circular init).
import { ImageShapeEnum } from "../../image/types";
import {
  type ITickerSettings,
  type ITickerSpecificSettings,
  TickerAnimDirectionEnum,
  TickerAnimSpeedEnum,
  TickerItemImageFitEnum,
} from "../types";
import { normalizeTickerWidth } from "./normalize-ticker-width";

export const ensureTickerElementSettings = (
  elementSettings: Record<string, unknown> | null = {},
) => {
  const {
    tickerItems,
    tickerItemsSpacing,
    tickerItemImageFit,
    shape,
    tickerAnimDirection,
    tickerAnimSpeed,
    tickerItemPadding,
  } = elementSettings as Partial<ITickerSettings>;

  const baseSettings = {
    horizontalAlignment: "center",
    round: "8px",
    backgroundColor: "",
    borderColor: "",
    borderWidth: 1,
    height: 180,
    shadow: "none",
    ...elementSettings,
    name: "Ticker",
    // Bare percentage number - consumers append the "%" themselves.
    width: normalizeTickerWidth(
      (elementSettings as Partial<ITickerSettings>)?.width,
    ),
  };

  const settings: ITickerSpecificSettings = {
    shape: ImageShapeEnum.Square,
    tickerItems: tickerItems ?? [],
    tickerItemImageFit: TickerItemImageFitEnum.Fit,
    tickerAnimDirection: TickerAnimDirectionEnum.Left,
    tickerAnimSpeed: TickerAnimSpeedEnum.Medium,
    tickerItemPadding: tickerItemPadding ?? 16,
    tickerItemsSpacing: tickerItemsSpacing ?? 8,
  };

  if (
    shape &&
    [
      ImageShapeEnum.Square,
      ImageShapeEnum.Rectangle,
      ImageShapeEnum.Circle,
    ].includes(shape)
  ) {
    settings.shape = shape;
  }

  if (
    tickerAnimSpeed &&
    [
      TickerAnimSpeedEnum.Fast,
      TickerAnimSpeedEnum.Medium,
      TickerAnimSpeedEnum.Slow,
    ].includes(tickerAnimSpeed)
  ) {
    settings.tickerAnimSpeed = tickerAnimSpeed;
  }

  if (
    tickerAnimDirection &&
    [
      TickerAnimDirectionEnum.Left,
      TickerAnimDirectionEnum.Right,
      TickerAnimDirectionEnum.Up,
      TickerAnimDirectionEnum.Down,
    ].includes(tickerAnimDirection)
  ) {
    settings.tickerAnimDirection = tickerAnimDirection;
  }

  if (
    tickerItemImageFit &&
    [TickerItemImageFitEnum.Fit, TickerItemImageFitEnum.Fill].includes(
      tickerItemImageFit,
    )
  ) {
    settings.tickerItemImageFit = tickerItemImageFit;
  }

  return { ...baseSettings, ...settings } as Required<ITickerSettings>;
};
